import {
  listDiscoverJobsPage,
  getOverview,
  getResultsFindingsSummary,
  getResultsPayloadForExport,
} from '../DataDiscovery/discoverApi';
import RDSIcon from 'react-aws-icons/dist/aws/logo/RDS';
import EC2Icon from 'react-aws-icons/dist/aws/logo/EC2';
import S3Icon from 'react-aws-icons/dist/aws/logo/S3';

import { FaServer, FaDatabase, FaFolderOpen } from 'react-icons/fa';
import { BsBraces } from 'react-icons/bs';
import { BRAND } from 'Utils/styleTokens';

const getSearchHistogram = async (dataSourceId) => {
  const numericId = Number(dataSourceId);
  if (!Number.isFinite(numericId)) return [];
  const allJobs = [];
  let page = 0;
  let totalPages = 1;
  const pageSize = 200;

  while (page < totalPages) {
    const { pageDto, discoverJobs } = await listDiscoverJobsPage(
      page, pageSize, 'startedAt,asc', numericId,
    );
    allJobs.push(...(discoverJobs || []));
    totalPages = pageDto && pageDto.totalPages != null ? Number(pageDto.totalPages) : 1;
    page += 1;
  }

  // SUCCEEDED is the normal series. A partial run (FAILED/INTERRUPTED/CANCELLED) carries real
  // but incomplete findings, so plotting it beside complete runs reads as a huge drop that
  // never happened — which is why it was excluded outright. But excluding it entirely left the
  // chart EMPTY for a source whose only run failed at 99%, which is worse: the user is told
  // nothing was ever discovered.
  //
  // So partial runs are included and MARKED. A consumer can render them distinctly (or drop
  // them) with the flag, instead of being silently handed a misleading shape or nothing at all.
  const PARTIAL_STATUSES = new Set(['FAILED', 'INTERRUPTED', 'CANCELLED', 'TIMEOUT', 'ERROR', 'STOPPED']);
  const sortedJobs = allJobs
    .filter((job) => job && job.id != null
      && (job.status === 'SUCCEEDED' || PARTIAL_STATUSES.has(job.status)))
    .sort((a, b) => {
      const timeA = Date.parse(a.finishedAt || a.startedAt || 0);
      const timeB = Date.parse(b.finishedAt || b.startedAt || 0);
      return timeA - timeB;
    });

  const series = [];
  for (let i = 0; i < sortedJobs.length; i++) {
    const job = sortedJobs[i];
    try {
      // Table-index (PeopleSoft-80K) runs: the sparse findings-summary lists every
      // sensitive field in ONE request — never reassemble the monolith here (hundreds
      // of page requests per run would hang the risk profile). Folder Search runs
      // persist only the folder INDEX (no tables) — getResultsPayloadForExport
      // iterates the folders and reassembles the per-file tables from chunks, so the
      // time series counts real sensitive fields instead of flatlining at zero.
      // Classic runs pass through as-is.
      let tables;
      const overview = await getOverview(job.id).catch(() => null);
      // The worker's precomputed summary answers this directly. Without it each point costs a
      // full chunk reassembly — 361 requests on the motivating run, and this loop runs PER
      // historical run, so a source with ten runs paid it ten times over.
      if (overview?.runSummary) {
        const rs = overview.runSummary;
        series.push({
          date: job.finishedAt || job.startedAt || new Date().toISOString(),
          sensitiveFieldCount: Number(rs.fields_with_findings ?? rs.items_with_findings) || 0,
          searchids: [String(job.id)],
          registeredServiceId: job.registeredServiceId || null,
          partial: job.status !== 'SUCCEEDED',
        });
        continue;
      }
      if (overview && overview.tableIndex) {
        const fs = await getResultsFindingsSummary(job.id).catch(() => null);
        tables = Array.isArray(fs?.tables) ? fs.tables : [];
      } else {
        const payload = (await getResultsPayloadForExport(job.id)) || {};
        tables = Array.isArray(payload.tables) ? payload.tables : [];
      }
      let sensitiveFieldCount = 0;

      tables.forEach((table) => {
        const fields = Array.isArray(table.fields) ? table.fields : [];
        fields.forEach((field) => {
          const findings = field && field.findings ? field.findings : {};
          if (Object.keys(findings).length > 0) sensitiveFieldCount += 1;
        });
      });

      series.push({
        date: job.finishedAt || job.startedAt || new Date().toISOString(),
        sensitiveFieldCount,
        searchids: [String(job.id)],
        registeredServiceId: job.registeredServiceId || null,
        partial: job.status !== 'SUCCEEDED',
      });
    } catch (e) {
      console.log('getSearchHistogram: failed to load job results', job.id, e);
    }
  }

  return series;
}

const sourceLabels = {
  'rds': 'RDS',
  'ec2': 'EC2',
  's3': 'S3',
  'external': 'External'
};

const sourceIconsAsElements = {
  'rds': <RDSIcon />,
  'ec2': <EC2Icon />,
  's3': <S3Icon />,
  'external': <FaServer />
};

const getDataSourceTypeIcon = (datasourceType) => {
  const type = String(datasourceType || '').toUpperCase();
  if (type === 'RELATIONAL') return <FaDatabase color={BRAND.blue} />;
  if (type === 'FILE_FOLDER') return <FaFolderOpen color={BRAND.blue} />;
  if (type === 'NOSQL') return <BsBraces color={BRAND.blue} />;
  return <FaServer color={BRAND.blue} />;
};

const getLandscapeIconForType = (sourceOrType) => {
  const value = String(sourceOrType || '').toLowerCase();
  if (value === 'relational') return getDataSourceTypeIcon('RELATIONAL');
  if (value === 'file_folder') return getDataSourceTypeIcon('FILE_FOLDER');
  if (value === 'nosql') return getDataSourceTypeIcon('NOSQL');
  if (value === 'rds') return sourceIconsAsElements.rds;
  if (value === 's3') return sourceIconsAsElements.s3;
  if (value === 'ec2') return sourceIconsAsElements.ec2;
  if (value === 'external') return <FaServer color={BRAND.blue} />;
  return getDataSourceTypeIcon(sourceOrType);
};

const jobFieldEntityCache = new Map();

const buildFieldEntityMapFromPayload = (payload = {}) => {
  const result = {};
  const tables = Array.isArray(payload.tables) ? payload.tables : [];
  tables.forEach((table) => {
    const tableName = table?.name;
    const fields = Array.isArray(table?.fields) ? table.fields : [];
    fields.forEach((field) => {
      const fieldName = field?.name;
      if (!tableName || !fieldName) return;
      const findings = field?.findings || {};
      result[`${tableName}.${fieldName}`] = new Set(Object.keys(findings));
    });
  });
  return result;
};

const getFieldEntityMapForJob = async (jobId) => {
  const id = String(jobId);
  if (jobFieldEntityCache.has(id)) return jobFieldEntityCache.get(id);
  // Same folder-chunk reassembly as the histogram: index-shaped Folder Search
  // payloads carry no tables, so diffing raw payloads would see zero fields.
  const payload = (await getResultsPayloadForExport(id)) || {};
  const map = buildFieldEntityMapFromPayload(payload);
  jobFieldEntityCache.set(id, map);
  return map;
};

// Diff findings directly from discover results payloads (no Elasticsearch).
const fetchDifferences = async (prevSearchids = [], searchids = []) => {
  try {
    const prevId = prevSearchids && prevSearchids[0];
    const currId = searchids && searchids[0];
    if (!prevId || !currId) return { newFindings: {}, removedFindings: {}, summary: { newTables: 0, newColumns: 0, reducedColumns: 0 } };

    const [prevMap, currMap] = await Promise.all([
      getFieldEntityMapForJob(prevId),
      getFieldEntityMapForJob(currId)
    ]);

    const allFields = new Set([
      ...Object.keys(prevMap),
      ...Object.keys(currMap)
    ]);
    const newFindings = {};
    const removedFindings = {};

    // Summary counters for the tooltip. Field keys are `table.field`, so the
    // portion before the first '.' is the table/folder and the remainder is the
    // column/file. A "new column" is one that had zero sensitive findings before
    // and has some now (present in curr, absent from prev); "reduced" is the
    // reverse. A "new table" is a table with no prior sensitive columns at all.
    const tableName = (fieldKey) => {
      const idx = fieldKey.indexOf('.');
      return idx === -1 ? fieldKey : fieldKey.slice(0, idx);
    };
    const prevTables = new Set(Object.keys(prevMap).map(tableName));
    const newColumns = new Set();
    const reducedColumns = new Set();
    const newTables = new Set();

    allFields.forEach((fieldKey) => {
      const prevEntities = prevMap[fieldKey] || new Set();
      const currEntities = currMap[fieldKey] || new Set();
      const added = [...currEntities].filter((entity) => !prevEntities.has(entity));
      const removed = [...prevEntities].filter((entity) => !currEntities.has(entity));
      if (added.length) newFindings[fieldKey] = added;
      if (removed.length) removedFindings[fieldKey] = removed;

      const hadFindings = prevEntities.size > 0;
      const hasFindings = currEntities.size > 0;
      if (!hadFindings && hasFindings) {
        newColumns.add(fieldKey);
        if (!prevTables.has(tableName(fieldKey))) newTables.add(tableName(fieldKey));
      } else if (hadFindings && !hasFindings) {
        reducedColumns.add(fieldKey);
      }
    });

    const summary = {
      newTables: newTables.size,
      newColumns: newColumns.size,
      reducedColumns: reducedColumns.size,
    };

    return { newFindings, removedFindings, summary };
  } catch(e) {
    console.log('fetchDifferences error', e);
    return { newFindings: {}, removedFindings: {}, summary: { newTables: 0, newColumns: 0, reducedColumns: 0 } };
  }
}

const transformDifferences = (differences) => differences || { newFindings: {}, removedFindings: {} };

const getDifferencesColumnNames = async (transformedDifferences) => (
  transformedDifferences || { newFindings: {}, removedFindings: {} }
);

const getSourcesMap = (landscapeData, unscannedSources) => {
  // console.log('getSourcesMap params', landscapeData, unscannedSources);
  const map = {
    rds: [],
    s3: [],
    ec2: [],
  };

  // if (landscapeData && landscapeData.length && unscannedSources && unscannedSources.length) {

  //   const landscapeDataValue = [...landscapeData, ...unscannedSources];
  //   console.log('getSourcesMap landscapeDataValue', landscapeDataValue);
  //   landscapeDataValue.forEach(data => {
  //     const source = data.source;
  //     if (map[source]) {
  //       map[source].push(data);
  //     } else {
  //       if (!map.external) map.external = [];
  //       map.external.push(data);
  //     }
  //   });
  //   }
  const landscapeDataValue = [...landscapeData, ...unscannedSources];
  // console.log('getSourcesMap landscapeDataValue', landscapeDataValue);
  landscapeDataValue.forEach(data => {
    const source = data.source;
    if (map[source]) {
      map[source].push(data);
    } else {
      if (!map.external) map.external = [];
      map.external.push(data);
    }
  });

  // console.log('getSourcesMap map', map);
  return map;
}

const filterByRegion = (lsData, region) => lsData.filter(data => data.region === region);
const filterByEngine = (lsData, engine) => lsData.filter(data => data.engine === engine);
const filterBySource = (lsData, source) => lsData.filter(data => data.source === source);

const filterFunctionMap = {
  'us-west-1': filterByRegion,
  'us-west-2': filterByRegion,
  'us-east-1': filterByRegion,
  'us-east-2': filterByRegion,
  'ca-central-1': filterByRegion,
  'ca-west-1': filterByRegion,
  'eu-central-1': filterByRegion,
  'eu-west-1': filterByRegion,
  'eu-west-2': filterByRegion,
  'eu-south-1': filterByRegion,
  'eu-west-3': filterByRegion,
  'eu-north-1': filterByRegion,
  'eu-central-2': filterByRegion,
  'mongodb': filterByEngine,
  'snowflake': filterByEngine,
  'rds': filterBySource,
  'ec2': filterBySource,
  's3': filterBySource,
  'external': filterBySource
};

const filterLandscapeData = (landscapeData, filter) => {
  const filterFunc = filterFunctionMap[filter] || filterBySource;
  const result = filterFunc(landscapeData, filter);
  return result;
}

export {
  fetchDifferences,
  filterLandscapeData,
  getDifferencesColumnNames,
  getSourcesMap,
  getSearchHistogram,
  transformDifferences,
  sourceLabels,
  sourceIconsAsElements,
  getDataSourceTypeIcon,
  getLandscapeIconForType
}
